import { NAMESPACE } from 'locale'
import { incidentReportsConstants } from 'locale/incidentReports'
import { useTranslation } from 'react-i18next'
import AttachmentLink from '../../attachments/AttachmentLink'
import { writtenFollowUpAttachments } from '../../attachments/caseAttachments'
import type { IncidentCaseV2ReadPanelProps } from '../../incidentCaseV2Steps'
import { useServiceUserWords } from '../../useServiceUserWords'
import { ReadGroup, ReadItem, ReadWide } from '../readLayout'
import {
  candourDetails,
  candourMethodLabels,
  hasWrittenFollowUp,
  keyRequirementLabels,
  namesOfIds,
  personsInformedLabels,
  savedDutyOfCandour,
  yesNo
} from '../readRules'
import { formatDate, formatDateOnly, formatTime } from '../readValues'
import { useTeamMemberName } from '../usePersonName'

const TEST_ID = 'incident-case-v2-view-duty-of-candour'

/**
 * Step 8, read only. Under No the answer and its details are shown; under Yes
 * every question answered is. A question asked by a tickbox is shown only
 * when that box is ticked.
 */
const DutyOfCandourReadPanel = ({
  incidentReport
}: IncidentCaseV2ReadPanelProps) => {
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const { singular: serviceUser } = useServiceUserWords()
  const { isLoading, nameOf } = useTeamMemberName()
  const saved = savedDutyOfCandour(incidentReport)

  const applicable = (
    <ReadItem
      label={t(incidentReportsConstants.CASE_V2_CANDOUR_APPLICABLE)}
      value={yesNo(t, saved?.isApplicable)}
      testId={`${TEST_ID}-applicable`}
    />
  )

  if (saved?.isApplicable === false) {
    return (
      <>
        {applicable}
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_CANDOUR_DETAILS)}
            value={candourDetails(saved)}
            testId={`${TEST_ID}-details`}
          />
        </ReadWide>
      </>
    )
  }
  if (!saved?.isApplicable) {
    return applicable
  }

  const { communication, outcomeShared } = saved

  return (
    <>
      {applicable}
      <ReadWide>
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_PERSON_INFORMED)}
          value={personsInformedLabels(t, saved, serviceUser)}
          testId={`${TEST_ID}-persons-informed`}
        />
      </ReadWide>

      <ReadGroup
        title={t(
          incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATION_DETAILS
        )}
        testId={`${TEST_ID}-communication`}
      >
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE)}
          value={formatDate(communication?.communicatedAt)}
          testId={`${TEST_ID}-communication-date`}
        />
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_TIME)}
          value={formatTime(communication?.communicatedAt)}
          testId={`${TEST_ID}-communication-time`}
        />
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_METHOD)}
          value={candourMethodLabels(t, communication)}
          testId={`${TEST_ID}-methods`}
        />
        <ReadItem
          isLoading={isLoading}
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_COMMUNICATED_BY)}
          value={namesOfIds(communication?.communicatedBy, nameOf)}
          testId={`${TEST_ID}-communicated-by`}
        />
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_KEY_REQUIREMENTS)}
          value={keyRequirementLabels(t, communication)}
          testId={`${TEST_ID}-key-requirements`}
        />
        {hasWrittenFollowUp(communication) && (
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE_PROVIDED)}
            value={formatDateOnly(communication?.writtenFollowUpProvidedAt)}
            testId={`${TEST_ID}-written-follow-up-at`}
          />
        )}
        {hasWrittenFollowUp(communication) && (
          <ReadWide>
            <ReadItem
              label={t(incidentReportsConstants.CASE_V2_ATTACHMENTS)}
              value={writtenFollowUpAttachments(communication).map((file) => (
                <AttachmentLink key={file.key} file={file} />
              ))}
              testId={`${TEST_ID}-written-follow-up-attachments`}
            />
          </ReadWide>
        )}
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_CANDOUR_NOTES)}
            value={communication?.notes}
            testId={`${TEST_ID}-communication-notes`}
          />
        </ReadWide>
      </ReadGroup>

      <ReadGroup
        title={t(incidentReportsConstants.CASE_V2_CANDOUR_OUTCOME_SHARED)}
        testId={`${TEST_ID}-outcome-shared`}
      >
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE_SHARED)}
          value={formatDateOnly(outcomeShared?.sharedAt)}
          testId={`${TEST_ID}-shared-at`}
        />
        <ReadItem
          isLoading={isLoading}
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_PERSON_RESPONSIBLE)}
          value={nameOf(outcomeShared?.responsibleUserId)}
          testId={`${TEST_ID}-responsible`}
        />
        <ReadWide>
          <ReadItem
            label={t(incidentReportsConstants.CASE_V2_CANDOUR_NOTES)}
            value={outcomeShared?.notes}
            testId={`${TEST_ID}-outcome-notes`}
          />
        </ReadWide>

        <ReadItem
          isLoading={isLoading}
          label={t(incidentReportsConstants.CASE_V2_VIEW_CANDOUR_COMPLETED_BY)}
          value={nameOf(saved.completedBy)}
          testId={`${TEST_ID}-completed-by`}
        />
        <ReadItem
          label={t(incidentReportsConstants.CASE_V2_CANDOUR_DATE_COMPLETED)}
          value={formatDateOnly(saved.completedAt)}
          testId={`${TEST_ID}-completed-at`}
        />
      </ReadGroup>
    </>
  )
}

export default DutyOfCandourReadPanel
